import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { STALE_TIMES } from "@/lib/query-performance";
import { useProject } from "@/components/project-context";
import { Page } from "@/components/layout/page";
import {
  ProjectIdentityTitle,
  ProjectIdentityActions,
  ProjectIdentityEditCard,
} from "@/components/project-identity-header";
import { RunDeltaStrip } from "@/components/experience/run-delta-strip";
import { WeakestAreasCard } from "@/components/hotspots/weakest-areas-card";
import { useCanEdit } from "@/lib/project-access";
import { ProjectActionRail } from "@/components/dashboard/project-action-rail";
import { LatestWalkthroughHero } from "@/components/dashboard/latest-walkthrough-hero";
import { RecentWalkthroughsCard } from "@/components/dashboard/recent-walkthroughs-card";
import { AgentWorkQueueCard } from "@/components/dashboard/agent-work-queue-card";
import type { WalkthroughListItem } from "@/components/dashboard/dashboard-types";

export function ProjectDashboardPage() {
  const project = useProject();
  const [editingIdentity, setEditingIdentity] = useState(false);
  const canEdit = useCanEdit();

  const {
    data: walkthroughsRaw,
    isLoading: walkthroughsLoading,
    isError: walkthroughsError,
    refetch: refetchWalkthroughs,
  } = trpc.walkthrough.listByProject.useQuery({ projectId: project.id });
  const walkthroughs = walkthroughsRaw as WalkthroughListItem[] | undefined;

  const { data: queueCounts, isLoading: queueCountsLoading } =
    trpc.finding.queueCounts.useQuery(
      { projectId: project.id },
      { staleTime: STALE_TIMES.list },
    );
  const openProblems = queueCounts?.openProblems ?? 0;
  const triageCount = queueCounts?.views?.triage ?? 0;
  const fixNextCount = queueCounts?.views?.fix_next ?? 0;
  const awaitingRerunCount = queueCounts?.views?.awaiting_rerun ?? 0;

  const { data: scenarios, isLoading: scenariosLoading } =
    trpc.scenario.list.useQuery(
      { projectId: project.id },
      { staleTime: STALE_TIMES.list },
    );
  const scenarioCount = scenarios?.length ?? 0;

  return (
    <Page
      title={<ProjectIdentityTitle project={project} />}
      description={project.description ?? undefined}
      descriptionClassName="line-clamp-1 max-w-3xl"
      maxWidth="max-w-[1240px]"
      actions={
        canEdit ? (
          <ProjectIdentityActions
            project={project}
            onEdit={() => setEditingIdentity(true)}
          />
        ) : undefined
      }
    >
      {canEdit && editingIdentity && (
        <ProjectIdentityEditCard
          project={project}
          onDone={() => setEditingIdentity(false)}
        />
      )}

      <div className="space-y-6">
        {/* Run-over-Run Delta & Freshness Strip */}
        <RunDeltaStrip
          projectId={project.id}
          awaitingRerunCount={awaitingRerunCount}
        />

        {/* 2-Column Command Center Grid */}
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start">
          {/* Main Column (65%): Latest Run Hero + Hotspots */}
          <div className="min-w-0 space-y-8 order-2 lg:order-1">
            {/* Latest Walkthrough Hero */}
            <LatestWalkthroughHero
              projectId={project.id}
              walkthroughs={walkthroughs}
              scenarioCount={scenarioCount}
              isLoading={walkthroughsLoading}
              isError={walkthroughsError}
              onRetry={() => refetchWalkthroughs()}
            />

            {/* Hotspots & Weakest Areas */}
            <WeakestAreasCard projectId={project.id} />

            {/* Recent Walkthroughs history */}
            <RecentWalkthroughsCard
              projectId={project.id}
              walkthroughs={walkthroughs}
            />
          </div>

          {/* Right Rail (35%): Action & Triage Queue - Hoisted on mobile/tablet */}
          <aside
            aria-label="Project actions"
            className="space-y-6 order-1 lg:order-2 lg:sticky lg:top-6"
          >
            <ProjectActionRail
              projectId={project.id}
              openProblems={openProblems}
              triageCount={triageCount}
              fixNextCount={fixNextCount}
              awaitingRerunCount={awaitingRerunCount}
              scenarioCount={scenarioCount}
              isLoading={queueCountsLoading || scenariosLoading}
            />

            <AgentWorkQueueCard projectId={project.id} />
          </aside>
        </div>
      </div>
    </Page>
  );
}
